import { USE_FEDERATED_AUTH } from 'config';
import { useStoreActions } from 'stores/global';
import { getUserData } from '../utils/storage';
import { useUser } from './useUser';
import { useLoginUsers, useSelectedOrg } from './federatedAuthHooks';
import { useLoginWithNavigation } from './useLoginWithNavigation';
import { reloadIntoNewContext } from '../utils/reloadIntoNewContext';
import type { LoginContext } from '../types';

// A switch lands the user on the app root and lets DefaultRoute pick the correct landing
// page for the new org's type (supplier → /suppliers, designer → /style), mirroring the
// login-time company-select flow. Hardcoding /style here stranded suppliers on the Styles
// list after switching into a supplier org (ticket DL-60481).
const SWITCH_LANDING_ROUTE = '/';

// Shared id so the trigger (opens) and the switch handler (closes) target the same dialog.
export const SWITCH_COMPANY_DIALOG_ID = 'switch-company';

// Auth-agnostic company row consumed by SwitchCompanyModal. The two auth flows
// (legacy forms + Auth0 federated) are normalized to this single shape so the UI
// never has to branch on USE_FEDERATED_AUTH.
export type SwitchableCompany = {
  // Stable key for React lists + search filtering (the app-user id).
  key: string;
  organizationName: string;
  type: 'designer' | 'supplier';
  isCurrent: boolean;
  // Federated only — the target Auth0 organization to mint an org-scoped token for.
  auth0OrgId?: string | null;
  // Legacy only — the raw context passed to the forms-auth company-select mutation.
  loginContext?: LoginContext;
};

/**
 * Surfaces the set of companies the signed-in user can switch between, and the action
 * to switch, working under BOTH auth flows:
 *
 * - Federated (Auth0): list from `GET /api/users/me/logins`; switching mints a new
 *   org-scoped access token via `selectOrg(auth0OrgId)`.
 * - Legacy (forms): list from the login contexts persisted at login; switching rewrites
 *   the Forms-auth cookie via the company-select mutation.
 *
 * Both branches then reload into the new context (see `reloadIntoNewContext`), landing on '/'.
 * All hooks are called unconditionally (mirroring `useUser`); the inactive branch's
 * hooks are safe no-ops because their context providers are only mounted for that flow.
 *
 * `onSwitchError` is injected by the caller (which already owns the notification API) rather
 * than importing `useNotification` here — that import would drag the notifications barrel into
 * this module and create an architecture cycle (dependency-cruiser `no-circular`).
 */
export const useCompanySwitcher = (onSwitchError?: () => void) => {
  const { remove } = useStoreActions(state => state.modalManager);

  // Federated (Auth0) sources — disabled under legacy auth (context defaults gate the query).
  const { data: loginUsers, isLoading: isLoadingFederated } = useLoginUsers();
  const { selectedOrgAuth0Id, selectOrg } = useSelectedOrg();

  // Legacy (forms) sources.
  const { userData } = getUserData();
  const { data: currentUser } = useUser();

  const closeDialog = () => remove(SWITCH_COMPANY_DIALOG_ID);

  // Legacy switch: the mutation's onSuccess reloads into the new context; close the dialog from
  // the same success callback so the two stay in step. onError surfaces a toast via the injected
  // callback (per .claude/rules/error-handling.md §2.2 — a failed mutation must notify).
  const { selectCompany, companySelect } = useLoginWithNavigation({
    onCompanySelectSuccessNavigateTo: SWITCH_LANDING_ROUTE,
    onCompanySelectSuccess: closeDialog,
    onCompanySelectError: onSwitchError,
    reloadOnCompanySelect: true,
  });

  // A switch is in flight only for the legacy (mutation) path; the federated path resolves
  // synchronously here (it navigates + closes before control returns).
  const isSwitching = companySelect.isPending;

  const federatedCompanies: SwitchableCompany[] = (loginUsers ?? []).map(user => ({
    key: user.id,
    organizationName: user.organization.name,
    type: user.organization.type,
    isCurrent:
      !!user.organization.auth0OrgId && user.organization.auth0OrgId === selectedOrgAuth0Id,
    auth0OrgId: user.organization.auth0OrgId,
  }));

  const legacyCompanies: SwitchableCompany[] = (userData?.loginContexts ?? [])
    .filter((context): context is NonNullable<LoginContext> => Boolean(context))
    .map(context => ({
      key: context.id,
      organizationName: context.organizationName,
      type: context.organizationType === 2 ? 'supplier' : 'designer',
      isCurrent: context.id === currentUser?.loginContext?.id,
      loginContext: context,
    }));

  const companies = USE_FEDERATED_AUTH ? federatedCompanies : legacyCompanies;
  const isLoading = USE_FEDERATED_AUTH ? isLoadingFederated : false;
  const hasMultiple = companies.length > 1;

  const switchTo = (company: SwitchableCompany): Promise<unknown> | void => {
    if (company.isCurrent) {
      closeDialog();
      return;
    }

    // Guard against a second switch firing while the first is still in flight — otherwise two
    // in-flight requests could resolve out of order and navigate/clear for the wrong company.
    if (isSwitching) return;

    if (USE_FEDERATED_AUTH) {
      // No Auth0 org id → this org can't be switched into via a scoped token; ignore.
      if (!company.auth0OrgId) return;
      try {
        // selectOrg persists the selection and writes the shared activeOrgAuth0Id cookie, so the
        // fresh document boots straight into the new org. Route through '/' so DefaultRoute lands
        // the correct page for the new org's type.
        selectOrg(company.auth0OrgId);
        closeDialog();
        reloadIntoNewContext(SWITCH_LANDING_ROUTE);
      } catch {
        onSwitchError?.();
      }
      return;
    }

    if (company.loginContext) {
      // The select endpoint authorizes off the session cookie, so no credentials are needed
      // here; navigation + cache clear + dialog close happen in the mutation's onSuccess, and a
      // failure surfaces via onCompanySelectError. The promise is returned so the caller can
      // keep its trigger disabled until the request settles.
      return selectCompany(company.loginContext, undefined, legacyCompanies.length);
    }
  };

  return { companies, hasMultiple, isLoading, isSwitching, switchTo };
};
